import { z } from 'zod'
import { type RoleData, type RoleId } from '../role'
import { TeamIdSchema, UserIdSchema, type EntityId } from '../ids'
import { AuditPropsSchema, type AuditProps } from '../common'
import { type UserScope } from './scopes'
import { PermissionEntityIdSchema } from './permission'
import {
  PermissionEntityScopeSchema,
  PermissionTypeSchema,
  type AllowedScopes,
  type PermissionEntityScope,
  type PermissionType,
} from './definition'

export type DefaultRole = Pick<RoleData, 'name' | 'roleId'> & WithPermissionOverrides

export type PermissionEntityId = EntityId | RoleId

export type WithPermissionEntityId = {
  entityId: PermissionEntityId
}

export type PermissionEntityIds = PermissionEntityId | (PermissionEntityId | undefined)[]

/**
 * @example Can update self (U.1) and downline team members from Team.1
 */
export type PermissionOverride<T extends PermissionType = PermissionType> = {
  [K in T]: {
    permissionType: K
    scope: AllowedScopes<K>
  }
}[T]

export type PermissionData = AuditProps & {
  readonly entityId: PermissionEntityId
  permissionType: PermissionType
  scope: PermissionEntityScope | 'inherit'
}

export type WithPermissionOverrides = {
  permissionOverrides: PermissionOverride<PermissionType>[]
}

export type WithPermissionData = {
  permissionOverrides?: PermissionData[]
}

export type ScopeWithSource = UserScope &
  AuditProps & {
    source: PermissionEntityId
  }

export type EditScope<T extends PermissionType> = AllowedScopes<T> | 'default'

export type PermissionListItem = {
  /** The most permissive permission scope for this entity */
  primaryScope?: ScopeWithSource
  /** All permission scopes in the permission map */
  allScopes: ScopeWithSource[]
  /** All overrides for this permission for the entity and their upline */
  allOverrides?: PermissionData[]
  /** All scopes this user could override the permission to */
  overridable: PermissionEntityScope[]
  /** The inherited permission scopes */
  inherits?: ScopeWithSource[]
}

export type PermissionList = Record<PermissionType, PermissionListItem>

// Zod Schemas

const UserScopeSelfSchema = z.object({
  entity: z.literal('self'),
})

const UserScopeDownlineSchema = z.object({
  entity: z.literal('downline'),
  entityId: z.union([UserIdSchema, TeamIdSchema]),
})

const UserScopeCompanySchema = z.object({
  entity: z.literal('company'),
})

const UserScopeWithinRestrictionsSchema = z.object({
  entity: z.literal('withinRestrictions'),
})

const UserScopeNoneSchema = z.object({
  entity: z.literal('none'),
})

const UserScopeSchema = z.discriminatedUnion('entity', [
  UserScopeSelfSchema,
  UserScopeDownlineSchema,
  UserScopeCompanySchema,
  UserScopeWithinRestrictionsSchema,
  UserScopeNoneSchema,
])

const ScopeWithSourceSchema = UserScopeSchema.and(AuditPropsSchema).and(
  z.object({
    source: PermissionEntityIdSchema,
  })
)

export const PermissionMapSchema = z.partialRecord(PermissionTypeSchema, z.array(ScopeWithSourceSchema))

export const PermissionDataSchema = AuditPropsSchema.extend({
  entityId: PermissionEntityIdSchema,
  permissionType: PermissionTypeSchema,
  scope: z.union([PermissionEntityScopeSchema, z.literal('inherit')]),
})

export const PermissionListItemSchema = z.object({
  primaryScope: ScopeWithSourceSchema.optional(),
  allScopes: z.array(ScopeWithSourceSchema),
  allOverrides: z.array(PermissionDataSchema).optional(),
  overridable: z.array(PermissionEntityScopeSchema),
  inherits: z.array(ScopeWithSourceSchema).optional(),
})

export const PermissionListSchema = z.record(PermissionTypeSchema, PermissionListItemSchema)

/** Represents a reduced form of a array of various PermissionOverride[] across a user's inheritance model */
export type PermissionMap = z.infer<typeof PermissionMapSchema>
export type WithPermissionMap = {
  permissionMap?: PermissionMap
}
